import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  FileText,
  TrendingUp,
  Award,
  CalendarDays
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '../components/Common/Button';
import Loader from '../components/Common/Loader';
import {
  setOverallResultSlice,
  setUserTestId
} from '../redux/Slices/StartTestSlice/startFulTest';
import { useAppDispatch, useAppSelector } from '../redux/store/hooks';
import { useLazyFetchCloudPrepUserTestsQuery } from '../services/cloudPrepApi';
import {
  useGetAllCompanyQuery,
  useLazyGetDriveTableResultsQuery,
  useLazyGetTestResultsQuery
} from '../services/startTestAPI/startTestApi';
import { getItem } from '../utils/storage';

const ResultsTablePage = ({
  userId,
  isDriveTest,
  isCloudPrepTest
}: {
  userId: string;
  isDriveTest?: boolean;
  isCloudPrepTest?: boolean;
}) => {
  const isCordinator = getItem('role') === '1005';
  const isFourColumnLayout = isDriveTest || isCloudPrepTest;
  const [getTestResults] = useLazyGetTestResultsQuery();
  const [getDriveTableResults] = useLazyGetDriveTableResultsQuery();
  const [getCloudPrepResults] = useLazyFetchCloudPrepUserTestsQuery();
  const { data: companyData } = useGetAllCompanyQuery({});
  const dispatch = useAppDispatch();
  const { overallresult } = useAppSelector(state => state.root.startFulltest);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalTests, setTotalTests] = useState(0);
  const [message, setMessage] = useState('');

  const handlePageSizeChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(0);
  };

  const renderPagination = () => {
    const totalPages = Math.ceil((totalTests || 0) / pageSize);
    const currentPageGroup = Math.floor(currentPage / 10);
    const pages = Array.from(
      { length: Math.min(10, totalPages - currentPageGroup * 10) },
      (_, i) => currentPageGroup * 10 + i
    );
    const handlePageChange = (newPage: number) => {
      setCurrentPage(newPage);
    };

    return (
      <div className='mt-6 flex flex-wrap items-center justify-between gap-4 rounded-b-xl border-t border-gray-200 px-6 py-4'>
        <div className='flex items-center gap-2'>
          <span className='text-sm text-gray-600'>Show</span>
          <select
            value={pageSize}
            onChange={handlePageSizeChange}
            className='cursor-pointer rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-all hover:border-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-20'
          >
            {[10, 20, 50, 100].map(size => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className='text-sm text-gray-600'>entries</span>
        </div>

        <div className='flex items-center gap-3'>
          <Button
            label={<ChevronLeft size={18} />}
            variant='none'
            classes='h-9 w-9 rounded-lg border border-gray-300 hover:border-primary hover:bg-primary hover:text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400'
            handleClickBtn={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
          />

          <div className='flex items-center gap-1'>
            {pages.map(page => (
              <Button
                key={page}
                label={page + 1}
                variant='none'
                classes={`h-9 min-w-[36px] px-3 rounded-lg font-medium text-sm transition-all ${
                  currentPage === page
                    ? 'bg-primary text-white shadow-md'
                    : 'text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
                handleClickBtn={() => handlePageChange(page)}
              />
            ))}
          </div>

          <Button
            label={<ChevronRight size={18} />}
            variant='none'
            classes='h-9 w-9 rounded-lg border border-gray-300 hover:border-primary hover:bg-primary hover:text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400'
            handleClickBtn={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages - 1}
          />
        </div>

        <div className='flex items-center gap-2 rounded-lg bg-gray-50 px-4 py-2'>
          <span className='text-sm text-gray-600'>Total Tests:</span>
          <span className='text-sm font-semibold text-primary'>
            {totalTests}
          </span>
        </div>
      </div>
    );
  };

  const handleViewResult = (testId: string) => {
    if (isCloudPrepTest) {
      navigate(`/cloud-prep-results/${testId}/${userId}/false`);
    } else if (isDriveTest) {
      navigate(`/drive-results/${testId}/${userId}`);
    } else {
      navigate(`/results/${testId}/${userId}`);
    }

    dispatch(setUserTestId(testId));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        let getTableResults;

        if (isCloudPrepTest) {
          getTableResults = getCloudPrepResults;
        } else if (isDriveTest) {
          getTableResults = getDriveTableResults;
        } else {
          getTableResults = getTestResults;
        }

        const { data, totaltests, totalpages, message } = await getTableResults(
          {
            page: currentPage,
            size: pageSize,
            userId
          }
        ).unwrap();
        dispatch(setOverallResultSlice(data));
        setTotalTests(totaltests);
        setMessage(message);
      } catch (error) {
        console.error('Failed to fetch test results:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [currentPage, pageSize, userId, isDriveTest, isCloudPrepTest]);

  return (
    <>
      <div className='ml-auto flex overflow-y-auto bg-primary md:w-[calc(100%-76px)]'></div>
      {isLoading ? (
        <div className='flex min-h-[55vh] items-center justify-center rounded-xl bg-white shadow-lg'>
          <Loader />
        </div>
      ) : (
        <div className='overflow-hidden rounded-xl bg-white shadow-lg'>
          <div className='min-h-[55vh] w-full'>
            <div className='sticky top-0 z-10 hidden md:block'>
              <div
                className={`min-w-4xl grid ${isFourColumnLayout ? 'grid-cols-4' : 'grid-cols-5'} gap-4 px-6 py-4`}
              >
                <div className='flex items-center justify-center gap-2'>
                  <FileText size={18} className='text-primary' />
                  <h3 className='text-[15px] font-semibold text-gray-700'>
                    Test Name
                  </h3>
                </div>
                <div className='flex items-center justify-center gap-2'>
                  <Calendar size={18} className='text-primary' />
                  <h3 className='text-[15px] font-semibold text-gray-700'>
                    Date Taken
                  </h3>
                </div>
                <div className='flex items-center justify-center gap-2'>
                  <TrendingUp size={18} className='text-primary' />
                  <h3 className='text-[15px] font-semibold text-gray-700'>
                    Score
                  </h3>
                </div>
                {!isFourColumnLayout && (
                  <div className='flex items-center justify-center gap-2'>
                    <CalendarDays size={18} className='text-primary' />
                    <h3 className='text-[15px] font-semibold text-gray-700'>
                      Year Of Pattern
                    </h3>
                  </div>
                )}
                <div className='flex items-center justify-center gap-2'>
                  <Award size={18} className='text-primary' />
                  <h3 className='text-[15px] font-semibold text-gray-700'>
                    Results
                  </h3>
                </div>
              </div>
            </div>

            {/* Data Rows */}
            <div className='px-4 py-2 md:px-6'>
              {overallresult?.length > 0 ? (
                <>
                  {/* Desktop View - Table Layout */}
                  <div className='hidden md:block'>
                    {overallresult.map((item: any, index: number) => (
                      <div
                        key={item?.testId}
                        className={`group grid ${isFourColumnLayout ? 'grid-cols-4' : 'grid-cols-5'} items-center gap-4 rounded-lg py-4 transition-all hover:bg-gray-50 hover:shadow-sm md:px-4 ${
                          index !== overallresult.length - 1
                            ? 'border-b border-gray-100'
                            : ''
                        }`}
                      >
                        <h3 className='break-words text-center text-[15px] font-medium text-gray-800'>
                          {item?.testName}
                        </h3>
                        <div className='flex items-center justify-center'>
                          <div className='rounded-lg bg-gray-100 px-4 py-2'>
                            <h3 className='text-[14px] font-medium text-gray-700'>
                              {item?.createdDate?.split('T')?.[0] ?? 'N/A'}
                            </h3>
                          </div>
                        </div>
                        <div className='flex items-center justify-center'>
                          <div className='rounded-lg bg-blue-50 px-4 py-2'>
                            <h3 className='text-[14px] font-semibold text-blue-700'>
                              {item?.totalQuestionsCorrect ?? 0}/
                              {item?.totalQuestionsGiven}
                            </h3>
                          </div>
                        </div>
                        {!isFourColumnLayout && (
                          <div className='flex items-center justify-center'>
                            <div className='rounded-lg bg-purple-50 px-4 py-2'>
                              <h3 className='text-[14px] font-medium text-purple-700'>
                                {item?.yearOfPatter ?? 'N/A'}
                              </h3>
                            </div>
                          </div>
                        )}
                        <div className='flex justify-center'>
                          <button
                            onClick={() => handleViewResult(item.id)}
                            className='group relative flex w-32 items-center justify-center gap-2 overflow-hidden rounded-lg border-2 bg-[#3366FF] px-2 py-2.5   text-[14px] font-semibold text-white transition-all hover:bg-white hover:text-primary hover:shadow-md active:scale-95'
                          >
                            <span className='relative z-10'>View Result</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Mobile View - Card Layout */}
                  <div className='space-y-4 md:hidden'>
                    {overallresult.map((item: any) => (
                      <div
                        key={item?.testId}
                        className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-all hover:shadow-md'
                      >
                        {/* Test Name */}
                        <div className='mb-3 border-b border-gray-100 pb-3'>
                          <h3 className='text-base font-semibold text-gray-800'>
                            {item?.testName}
                          </h3>
                        </div>

                        {/* Info Grid */}
                        <div className='space-y-3'>
                          {/* Date */}
                          <div className='flex items-center justify-between'>
                            <div className='flex items-center gap-2'>
                              <Calendar size={16} className='text-primary' />
                              <span className='text-sm font-medium text-gray-600'>
                                Date Taken
                              </span>
                            </div>
                            <div className='rounded-lg bg-gray-100 px-3 py-1.5'>
                              <span className='text-sm font-medium text-gray-700'>
                                {item?.createdDate?.split('T')?.[0] ?? 'N/A'}
                              </span>
                            </div>
                          </div>

                          {/* Score */}
                          <div className='flex items-center justify-between'>
                            <div className='flex items-center gap-2'>
                              <TrendingUp size={16} className='text-primary' />
                              <span className='text-sm font-medium text-gray-600'>
                                Score
                              </span>
                            </div>
                            <div className='rounded-lg bg-blue-50 px-3 py-1.5'>
                              <span className='text-sm font-semibold text-blue-700'>
                                {item?.totalQuestionsCorrect ?? 0}/
                                {item?.totalQuestionsGiven}
                              </span>
                            </div>
                          </div>

                          {/* Year of Pattern */}
                          {!isFourColumnLayout && (
                            <div className='flex items-center justify-between'>
                              <div className='flex items-center gap-2'>
                                <CalendarDays
                                  size={16}
                                  className='text-primary'
                                />
                                <span className='text-sm font-medium text-gray-600'>
                                  Year Of Pattern
                                </span>
                              </div>
                              <div className='rounded-lg bg-purple-50 px-3 py-1.5'>
                                <span className='text-sm font-medium text-purple-700'>
                                  {item?.yearOfPatter ?? 'N/A'}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Action Button */}
                        <button
                          onClick={() => handleViewResult(item.id)}
                          className='mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-[#3366FF] px-4 py-2.5   text-sm font-semibold text-white transition-all hover:bg-[#2952CC] active:scale-95'
                        >
                          <Award size={16} />
                          <span>View Result</span>
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className='flex min-h-[40vh] flex-col items-center justify-center px-4'>
                  <div className='rounded-full bg-gray-100 p-6'>
                    <FileText size={48} className='text-gray-400' />
                  </div>
                  <p className='mt-4 text-center text-base font-medium text-gray-600 md:text-lg'>
                    {isCordinator
                      ? 'This User haven`t Taken any Tests'
                      : 'Your test results will appear here once you complete a test'}
                  </p>
                </div>
              )}
            </div>
          </div>
          {overallresult?.length > 0 && renderPagination()}
        </div>
      )}
    </>
  );
};

export default ResultsTablePage;
